// İSG uzmanı portalı — {ad, kod?} formu (Modal + RHF + Zod). Şube ve saha aynı
// sözleşmeyi paylaşır (api-contract §9.5/§9.6: `{ name, code? }`). Mutasyon çağrısı
// `submit` ile dışarıdan verilir; başarıda `onSuccess` (liste invalidasyonu + bildirim).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  Spinner,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation } from '@tanstack/react-query';
import { type ReactNode, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export interface NameCodeValues {
  name: string;
  code: string | null;
}

interface NameCodeFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  nameLabel: string;
  codeLabel: string;
  isEdit: boolean;
  initial?: { name: string; code: string | null } | null;
  submit: (values: NameCodeValues) => Promise<unknown>;
  onSuccess?: () => void;
}

export function NameCodeFormModal({
  open,
  onOpenChange,
  title,
  nameLabel,
  codeLabel,
  isEdit,
  initial,
  submit,
  onSuccess,
}: NameCodeFormModalProps) {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t('admin.validation.nameRequired')),
        code: z.string().trim().optional(),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: { name: initial?.name ?? '', code: initial?.code ?? '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      submit({
        name: values.name,
        code: values.code && values.code.length > 0 ? values.code : null,
      }),
    onSuccess: () => {
      reset();
      onSuccess?.();
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
        }
        onOpenChange(next);
      }}
      title={title}
      closeLabel={t('admin.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('admin.common.cancel')}
          </Button>
          <Button type="submit" form="namecode-form" loading={mutation.isPending}>
            {mutation.isPending ? (
              <Spinner label={t('admin.common.save')} />
            ) : isEdit ? (
              t('admin.common.save')
            ) : (
              t('admin.common.create')
            )}
          </Button>
        </>
      }
    >
      <form
        id="namecode-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        <Field
          htmlFor="namecode-name"
          label={nameLabel}
          error={formState.errors.name?.message}
          required
        >
          <Input
            id="namecode-name"
            invalid={Boolean(formState.errors.name)}
            {...register('name')}
          />
        </Field>

        <Field htmlFor="namecode-code" label={codeLabel}>
          <Input id="namecode-code" {...register('code')} />
        </Field>
      </form>
    </Modal>
  );
}
